import { useEffect, useState } from "react";

import { Add, Archive, Settings, Warning } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  getCrimeCodes,
  postCrimeCodeArchive,
  CrimeCodeModel,
} from "@rubi/api-generated";
import { toast } from "@rubi/ui";
import { useParams, useNavigate } from "react-router-dom";

import CrimeCodeEditDialog from "./CrimeCodeEditDialog";
import CrimeCodeRulesDialog from "./CrimeCodeRulesDialog";

const CrimeCodeManagement: React.FC = () => {
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const [crimeCodes, setCrimeCodes] = useState<CrimeCodeModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [rulesDialogOpen, setRulesDialogOpen] = useState(false);
  const [selectedCode, setSelectedCode] = useState<CrimeCodeModel | null>(null);
  const [selectedCodeForRules, setSelectedCodeForRules] =
    useState<CrimeCodeModel | null>(null);
  const [codesInUse, setCodesInUse] = useState<Set<string>>(new Set());
  const [archiveConfirmOpen, setArchiveConfirmOpen] = useState(false);
  const [codeToArchive, setCodeToArchive] = useState<CrimeCodeModel | null>(
    null,
  );

  useEffect(() => {
    fetchCrimeCodes();
  }, [orgId, showArchived]);

  const fetchCrimeCodes = async () => {
    if (!orgId) return;
    try {
      setLoading(true);
      // The generated client types the crime-codes endpoint with `query?: never`,
      // but the backend accepts an `include_archived` filter. Cast to the corrected
      // options shape so the filter is sent without loosening the whole call to `any`.
      const options = {
        path: { org_id: orgId },
        query: { include_archived: showArchived },
      } as unknown as Parameters<typeof getCrimeCodes>[0];
      const response = await getCrimeCodes(options);
      setCrimeCodes(response.data || []);
    } catch (error) {
      console.error("Error fetching crime codes:", error);
      toast.error("Failed to load crime codes");
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setSelectedCode(null);
    setEditDialogOpen(true);
  };

  const handleEdit = (code: CrimeCodeModel) => {
    setSelectedCode(code);
    setEditDialogOpen(true);
  };

  const handleArchive = (code: CrimeCodeModel) => {
    setCodeToArchive(code);
    setArchiveConfirmOpen(true);
  };

  const handleArchiveConfirm = async () => {
    if (!orgId || !codeToArchive) return;
    try {
      await postCrimeCodeArchive({
        path: { org_id: orgId, crime_code_id: codeToArchive.id },
      });
      toast.success("Crime code archived");
      fetchCrimeCodes();
      setArchiveConfirmOpen(false);
      setCodeToArchive(null);
    } catch (error) {
      console.error("Error archiving crime code:", error);
      toast.error("Failed to archive crime code");
    }
  };

  const handleArchiveCancel = () => {
    setArchiveConfirmOpen(false);
    setCodeToArchive(null);
  };

  const handleManageRules = (code: CrimeCodeModel) => {
    setSelectedCodeForRules(code);
    setRulesDialogOpen(true);
  };

  const handleRulesDialogClose = () => {
    setRulesDialogOpen(false);
    setSelectedCodeForRules(null);
  };

  const handleDialogClose = () => {
    setEditDialogOpen(false);
    setSelectedCode(null);
    fetchCrimeCodes();
  };

  const app = location.pathname.includes("rubiintel")
    ? "rubiintel"
    : "rubireportv2";

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h4">Crime Codes</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={handleAdd}>
          Add Crime Code
        </Button>
      </Box>

      <Card sx={{ p: 2, mb: 2 }}>
        <Button
          variant={showArchived ? "outlined" : "contained"}
          onClick={() => setShowArchived(!showArchived)}
        >
          {showArchived ? "Hide Archived" : "Show Archived"}
        </Button>
      </Card>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>RMS Code</TableCell>
              <TableCell>RMS Code Extension</TableCell>
              <TableCell>Jurisdiction</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Report Types</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {crimeCodes.map((code) => {
              const canEdit = !codesInUse.has(code.id) && !code.is_archived;
              return (
                <TableRow key={code.id}>
                  <TableCell>{code.title}</TableCell>
                  <TableCell>{code.rms_code}</TableCell>
                  <TableCell>{code.rms_code_extension || "-"}</TableCell>
                  <TableCell>{code.jurisdiction || "-"}</TableCell>
                  <TableCell>{code.description || "-"}</TableCell>
                  <TableCell>
                    {(() => {
                      const classes = code.visible_on_survey_classes;
                      if (!classes) return "All Reports";

                      // Handle both array and object formats
                      let classList: string[] = [];
                      if (Array.isArray(classes)) {
                        classList = classes;
                      } else if (typeof classes === "object") {
                        classList = Object.keys(classes);
                      }

                      return classList.length > 0 ? (
                        <Box
                          sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}
                        >
                          {classList.map((sc) => (
                            <Chip key={sc} label={sc} size="small" />
                          ))}
                        </Box>
                      ) : (
                        "All Reports"
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    {code.is_archived ? (
                      <Chip label="Archived" color="default" size="small" />
                    ) : (
                      <Chip label="Active" color="primary" size="small" />
                    )}
                  </TableCell>
                  <TableCell>
                    {!code.is_archived && (
                      <>
                        <Tooltip title="Manage Rules">
                          <IconButton
                            size="small"
                            onClick={() => handleManageRules(code)}
                          >
                            <Settings />
                          </IconButton>
                        </Tooltip>
                        <IconButton
                          size="small"
                          onClick={() => handleArchive(code)}
                        >
                          <Archive />
                        </IconButton>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <CrimeCodeEditDialog
        open={editDialogOpen}
        onClose={handleDialogClose}
        crimeCode={selectedCode}
        orgId={orgId ?? ""}
      />
      {selectedCodeForRules && (
        <CrimeCodeRulesDialog
          open={rulesDialogOpen}
          onClose={handleRulesDialogClose}
          crimeCodeId={selectedCodeForRules.id}
          orgId={orgId ?? ""}
        />
      )}

      <Dialog open={archiveConfirmOpen} onClose={handleArchiveCancel}>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <Warning color="warning" />
            Archive Crime Code?
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to archive &quot;{codeToArchive?.title}&quot;?
          </DialogContentText>
        </DialogContent>
        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "space-between",
            padding: "16px",
          }}
        >
          <Button
            onClick={handleArchiveCancel}
            color="secondary"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleArchiveConfirm}
            color="primary"
            variant="contained"
          >
            Archive
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CrimeCodeManagement;
